import axios from 'axios';

// In production, use relative URLs since API and frontend are served from same origin
const isProduction = process.env.NODE_ENV === 'production' || !process.env.REACT_APP_API_URL;
const WEB_API_URL = process.env.REACT_APP_API_URL || (isProduction ? '/api' : 'http://localhost:5000/api');
const WEB_IMAGE_URL = process.env.REACT_APP_IMAGE_URL || (isProduction ? '' : 'http://localhost:5000');
// Capacitor native app needs an absolute URL since there's no web server origin
const CAP_API_URL = process.env.REACT_APP_CAPACITOR_API_URL || 'https://smartposafrica.com/api';
const CAP_IMAGE_URL = process.env.REACT_APP_CAPACITOR_IMAGE_URL || 'https://smartposafrica.com';

// Lazy check – Capacitor bridge may not be ready at module init
const getIsCapacitor = () => typeof window !== 'undefined' && window.Capacitor && window.Capacitor.isNativePlatform();

export const getImageBaseUrl = () => getIsCapacitor() ? CAP_IMAGE_URL : WEB_IMAGE_URL;
export const IMAGE_BASE_URL = WEB_IMAGE_URL; // static fallback for existing imports

const api = axios.create({
  baseURL: WEB_API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Override baseURL at request time for Capacitor (bridge may not be ready at module init)
api.interceptors.request.use((config) => {
  if (getIsCapacitor()) {
    config.baseURL = CAP_API_URL;
  }
  // Prefer impersonation token (sessionStorage, tab-scoped) over regular token
  const token = sessionStorage.getItem('impersonationToken') || localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Skip redirect for auth endpoints - let the app handle authentication flow
      const requestUrl = error.config?.url || '';
      if (requestUrl.includes('/auth/login') || requestUrl.includes('/auth/me')) {
        return Promise.reject(error);
      }

      // Skip redirect if on public pages (landing pages, login, signup, etc.)
      const currentPath = window.location.pathname;
      const publicPaths = ['/', '/login', '/signup', '/features', '/pricing', '/about', '/reset-password'];
      if (publicPaths.includes(currentPath)) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        return Promise.reject(error);
      }

      // Get user info before clearing
      const userStr = localStorage.getItem('user');
      let redirectUrl = '/';

      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          const slug = user.tenantSlug || (user.isSuperAdmin ? 'admin' : null);
          if (slug) {
            redirectUrl = `/${slug}/login`;
          }
        } catch (e) {
          // Ignore parse errors
        }
      }

      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = redirectUrl;
    }
    return Promise.reject(error);
  }
);

// Storefront API – public, no auth required
export const storefrontAPI = {
  getStore: (slug) => api.get(`/storefront/${slug}`),
  getBranchStore: (slug, branchSlug) => api.get(`/storefront/${slug}/${branchSlug}`),
  getBranches: (slug) => api.get(`/storefront/${slug}/branches`),
  getProducts: (slug, params) => api.get(`/storefront/${slug}/products`, { params }),
  placeOrder: (slug, data) => api.post(`/storefront/${slug}/orders`, data),
  trackOrder: (slug, phone, orderNumber) => api.get(`/storefront/${slug}/track`, { params: { phone, ...(orderNumber ? { orderNumber } : {}) } }),
};

// Store Manager API – owner/manager
export const storeManagerAPI = {
  getSettings: () => api.get('/store/settings'),
  updateSettings: (data) => api.put('/store/settings', data),
  uploadHeroImage: (formData) => api.post('/store/settings/hero-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getBranchSettings: (branchId) => api.get(`/store/settings/branch/${branchId}`),
  updateBranchSettings: (branchId, data) => api.put(`/store/settings/branch/${branchId}`, data),
  uploadBranchHeroImage: (branchId, formData) => api.post(`/store/settings/branch/${branchId}/hero-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getOrders: (params) => api.get('/store/orders', { params }),
  getOrder: (id) => api.get(`/store/orders/${id}`),
  updateOrderStatus: (id, status) => api.patch(`/store/orders/${id}/status`, { status }),
  getStats: (params) => api.get('/store/stats', { params }),
  getNotifications: () => api.get('/store/notifications'),
};

export const notificationsAPI = {
  list: () => api.get('/notifications'),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Auth API
export const authAPI = {
  login: (username, password, tenantSlug) => api.post('/auth/login', { username, password, tenantSlug }),
  getMe: () => api.get('/auth/me'),
  getTenantBySlug: (slug) => api.get(`/auth/tenant/${slug}`),
  searchTenants: (query) => api.get('/auth/tenants/search', { params: { q: query } }),
  searchBusinesses: (username, password, query) => api.post('/auth/search-businesses', { username, password, query }),
  requestPasswordReset: (email, tenantSlug) => api.post('/auth/forgot-password', { email, tenantSlug }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, newPassword })
};

// Products API
export const productsAPI = {
  getAll: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => {
    // Check if data is FormData (has image), otherwise use JSON
    if (data instanceof FormData) {
      return api.post('/products', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return api.post('/products', data);
  },
  update: (id, data) => {
    // Check if data is FormData (has image), otherwise use JSON
    if (data instanceof FormData) {
      return api.put(`/products/${id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return api.put(`/products/${id}`, data);
  },
  getLowStock: () => api.get('/products/inventory/low-stock'),
  delete: (id) => api.delete(`/products/${id}`),
  // 86'd (temporarily unavailable) management
  toggle86: (id) => api.put(`/products/${id}/86`),
  get86d: () => api.get('/products/status/86d'),
  // Box item & restock methods
  search: (query, branchId) => api.get('/products/search', { params: { q: query, branchId } }),
  restock: (id, data) => api.post(`/products/${id}/restock`, data),
  getPurchaseHistory: (id, limit = 20) => api.get(`/products/${id}/purchase-history`, { params: { limit } }),
  updateBatchPrice: (productId, batchId, sellingPrice) => api.patch(`/products/${productId}/batch/${batchId}`, { sellingPrice }),
  // Bulk upload
  bulkPreview: (formData) => api.post('/products/bulk/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  bulkUpload: (formData) => api.post('/products/bulk/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadTemplate: () => api.get('/products/bulk/template', { responseType: 'blob' })
};

// Sales API
export const salesAPI = {
  getAll: (params) => api.get('/sales', { params }),
  getById: (id) => api.get(`/sales/${id}`),
  create: (data) => api.post('/sales', data),
  void: (id, data) => api.delete(`/sales/${id}`, { data }),
  reinstate: (id, data) => api.patch(`/sales/${id}/reinstate`, data),
  completePayment: (id, data) => api.patch(`/sales/${id}/complete-payment`, data)
};

// Expenses API
export const expensesAPI = {
  getAll: (params) => api.get('/expenses', { params }),
  getById: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post('/expenses', data)
};

// Reports API
export const reportsAPI = {
  getDashboard: (params) => api.get('/reports/dashboard', { params }),
  getDailySales: (params) => api.get('/reports/daily-sales', { params }),
  getSalesTrends: (params) => api.get('/reports/sales-trends', { params }),
  getStaffPerformance: (params) => api.get('/reports/staff-performance', { params }),
  getStylistPerformance: (params) => api.get('/reports/stylist-performance', { params })
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  resetPassword: (id, newPassword) => api.post(`/users/${id}/reset-password`, { newPassword }),
  uploadImage: (id, formData) => api.post(`/users/${id}/upload-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteImage: (id) => api.delete(`/users/${id}/image`),
  // Profile (self-service)
  getProfile: () => api.get('/users/me/profile'),
  updateProfile: (data) => api.put('/users/me/profile', data),
  uploadProfileImage: (formData) => api.post('/users/me/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteProfileImage: () => api.delete('/users/me/image')
};

// Tenants API
export const tenantsAPI = {
  getCurrent: () => api.get('/tenants/current'),
  updateSubscription: (data) => api.put('/tenants/subscription', data)
};

// Audit API
export const auditAPI = {
  getAll: (params) => api.get('/audit', { params })
};

// Security Requests API
export const securityRequestsAPI = {
  getAll: (params) => api.get('/security-requests', { params }),
  getById: (id) => api.get(`/security-requests/${id}`),
  create: (data) => api.post('/security-requests', data),
  approve: (id) => api.post(`/security-requests/${id}/approve`),
  reject: (id) => api.post(`/security-requests/${id}/reject`)
};

// Customers API
export const customersAPI = {
  search: (q) => api.get('/customers/search', { params: { q } }),
  getAll: (params) => api.get('/customers', { params }),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`)
};

// Applications API (Public)
export const applicationsAPI = {
  signup: (data) => api.post('/applications/signup', data),
  sendOTP: (email) => api.post('/applications/send-otp', { email }),
  verifyOTP: (email, code) => api.post('/applications/verify-otp', { email, code }),
  checkBusinessName: (name) => api.get('/applications/check-business-name', { params: { name } })
};

// Super Admin API
export const superAdminAPI = {
  getDashboard: () => api.get('/super-admin/dashboard'),
  getApplications: (params) => api.get('/super-admin/applications', { params }),
  getApplication: (id) => api.get(`/super-admin/applications/${id}`),
  checkSlugAvailability: (slug) => api.get(`/super-admin/check-slug/${slug}`),
  approveApplication: (id, slug, subscriptionMonths) =>
    api.post(`/super-admin/applications/${id}/approve`, { slug, subscriptionMonths }),
  rejectApplication: (id, reason) =>
    api.post(`/super-admin/applications/${id}/reject`, { reason }),
  revertApplication: (id) =>
    api.post(`/super-admin/applications/${id}/revert`),
  getTenants: (params) => api.get('/super-admin/tenants', { params }),
  getTenant: (id) => api.get(`/super-admin/tenants/${id}`),
  getTenantFullData: (id, params) => api.get(`/super-admin/tenants/${id}/full-data`, { params }),
  updateTenantStatus: (id, isActive) =>
    api.put(`/super-admin/tenants/${id}/status`, { isActive }),
  extendSubscription: (id, data) =>
    api.put(`/super-admin/tenants/${id}/subscription`, data),
  updateTenantSlug: (id, slug) =>
    api.put(`/super-admin/tenants/${id}/slug`, { slug }),
  updateTenantBusinessType: (id, businessType, businessSubtype) =>
    api.put(`/super-admin/tenants/${id}/business-type`, { businessType, businessSubtype }),
  deleteTenant: (id, confirmationName) =>
    api.delete(`/super-admin/tenants/${id}`, { data: { confirmationName } }),
  resetOwnerPassword: (id) =>
    api.post(`/super-admin/tenants/${id}/reset-owner-password`),
  deleteBranch: (id, data) =>
    api.delete(`/super-admin/branches/${id}`, { data }),
  setMainBranch: (id) =>
    api.post(`/super-admin/branches/${id}/set-main`),
  updateBranchStatus: (id, isActive) =>
    api.put(`/super-admin/branches/${id}/status`, { isActive }),
  // Global Analytics
  getAnalytics: (params) => api.get('/super-admin/analytics', { params }),
  getRevenueByTenant: (params) => api.get('/super-admin/analytics/revenue-by-tenant', { params }),
  getSubscriptionHealth: () => api.get('/super-admin/analytics/subscription-health'),
  getStaffProductivity: (params) => api.get('/super-admin/analytics/staff-productivity', { params }),
  getIndustryPerformance: () => api.get('/super-admin/analytics/industry-performance'),
  getAnomalies: () => api.get('/super-admin/analytics/anomalies'),
  // Oversight - All Transactions
  getAllTransactions: (params) => api.get('/super-admin/oversight/transactions', { params }),
  getVoidedSales: (params) => api.get('/super-admin/oversight/voids', { params }),
  getSuspiciousActivity: (params) => api.get('/super-admin/oversight/suspicious', { params }),
  // Branch Requests
  getBranchRequests: (params) => api.get('/super-admin/branch-requests', { params }),
  approveBranchRequest: (id) => api.post(`/super-admin/branch-requests/${id}/approve`),
  rejectBranchRequest: (id, reason) => api.post(`/super-admin/branch-requests/${id}/reject`, { reason }),
  // Audit Logs
  getAuditLogs: (params) => api.get('/super-admin/audit-logs', { params }),
  // Bulk Actions
  bulkUpdateTenantStatus: (ids, isActive) => api.post('/super-admin/tenants/bulk-status', { ids, isActive }),
  bulkExtendSubscriptions: (ids, days) => api.post('/super-admin/tenants/bulk-extend', { ids, days })
};

// Platform API (Infrastructure Management)
export const platformAPI = {
  // Subscription Tiers
  getTiers: () => api.get('/platform/tiers'),
  createTier: (data) => api.post('/platform/tiers', data),
  updateTier: (id, data) => api.put(`/platform/tiers/${id}`, data),
  deleteTier: (id) => api.delete(`/platform/tiers/${id}`),
  assignTierToTenant: (tenantId, tierId) => api.put(`/platform/tenants/${tenantId}/tier`, { tierId }),

  // Features
  getFeatures: () => api.get('/platform/features'),
  createFeature: (data) => api.post('/platform/features', data),
  updateTierFeatures: (tierId, featureIds) => api.put(`/platform/tiers/${tierId}/features`, { featureIds }),
  checkFeatureAccess: (code) => api.get(`/platform/check-feature/${code}`),

  // Grace Period
  getGracePeriodTenants: () => api.get('/platform/grace-period/tenants'),
  setGracePeriod: (tenantId, graceDays) => api.put(`/platform/tenants/${tenantId}/grace-period`, { graceDays }),
  enforceLockout: () => api.post('/platform/grace-period/enforce'),

  // Tax Configuration
  getTaxConfigs: (params) => api.get('/platform/tax-configs', { params }),
  createTaxConfig: (data) => api.post('/platform/tax-configs', data),
  updateTaxConfig: (id, data) => api.put(`/platform/tax-configs/${id}`, data),
  pushTaxRates: (country, taxRate) => api.post('/platform/tax-configs/push', { country, taxRate }),

  // Admin Impersonation
  startImpersonation: (tenantId, reason) => api.post('/platform/impersonate', { tenantId, reason }),
  endImpersonation: (logId, actionsPerformed) => api.post(`/platform/impersonate/${logId}/end`, { actionsPerformed }),
  getImpersonationLogs: (params) => api.get('/platform/impersonation-logs', { params }),

  // Tenant Health
  getTenantHealth: () => api.get('/platform/tenant-health'),

  // Seed Defaults
  seedDefaults: () => api.post('/platform/seed-defaults'),

  // Platform Settings & Exchange Rates
  getSettings: () => api.get('/platform/settings'),
  updateSettings: (data) => api.put('/platform/settings', data),
  getExchangeRates: () => api.get('/platform/exchange-rates'),
  refreshExchangeRates: () => api.post('/platform/exchange-rates/refresh')
};

// Market Intelligence API
export const marketIntelligenceAPI = {
  // Product Affinity & Basket Analysis
  getBasketAnalysis: (params) => api.get('/market-intelligence/basket-analysis', { params }),

  // Brand Market Share
  getBrandShare: (params) => api.get('/market-intelligence/brand-share', { params }),

  // Geospatial Spending Analysis
  getSpendingByLocation: (params) => api.get('/market-intelligence/spending-by-location', { params }),

  // Peak Hours Analysis
  getPeakHours: (params) => api.get('/market-intelligence/peak-hours', { params }),

  // Price Elasticity
  getPriceElasticity: (params) => api.get('/market-intelligence/price-elasticity', { params }),

  // Data Export (Data-as-a-Service)
  exportSummary: (params) => api.get('/market-intelligence/export/summary', { params }),
  exportTrends: (params) => api.get('/market-intelligence/export/trends', { params }),

  // Download CSV
  downloadSummaryCSV: async (period = 30) => {
    const response = await api.get('/market-intelligence/export/summary', {
      params: { period, format: 'csv' },
      responseType: 'blob'
    });
    return response;
  }
};

// Owner API
export const ownerAPI = {
  // Dashboard
  getDashboard: (params) => api.get('/owner/dashboard', { params }),
  // Staff Management
  getStaff: (params) => api.get('/owner/staff', { params }),
  createStaff: (data) => api.post('/owner/staff', data),
  updateStaff: (id, data) => api.put(`/owner/staff/${id}`, data),
  deleteStaff: (id) => api.delete(`/owner/staff/${id}`),
  resetStaffPassword: (id, newPassword) => api.post(`/owner/staff/${id}/reset-password`, { newPassword }),
  uploadStaffImage: (id, formData) => api.post(`/users/${id}/upload-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteStaffImage: (id) => api.delete(`/users/${id}/image`),
  // Activity Logs
  getActivity: (params) => api.get('/owner/activity', { params }),
  exportActivity: (params) => api.get('/owner/activity/export', { params, responseType: 'blob' }),
  // Reports
  getPLReport: (params) => api.get('/owner/reports/pl', { params }),
  getBranchComparison: (params) => api.get('/owner/reports/branch-comparison', { params }),
  getProductProfitability: (params) => api.get('/owner/reports/product-profitability', { params }),
  getStaffPerformance: (params) => api.get('/owner/reports/staff-performance', { params }),
  // Stylist Performance (SERVICES/SALON business types)
  getStylistPerformance: (params) => api.get('/owner/reports/stylist-performance', { params }),
  // Settings
  getSettings: () => api.get('/owner/settings'),
  updateSettings: (data) => api.put('/owner/settings', data),
  // Logo Management
  uploadLogo: (formData) => api.post('/owner/settings/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteLogo: () => api.delete('/owner/settings/logo')
};

// Stock Transfers API (Retail Module)
export const stockTransfersAPI = {
  getAll: (params) => api.get('/stock-transfers', { params }),
  getById: (id) => api.get(`/stock-transfers/${id}`),
  create: (data) => api.post('/stock-transfers', data),
  ship: (id) => api.post(`/stock-transfers/${id}/ship`),
  receive: (id, items) => api.post(`/stock-transfers/${id}/receive`, { items }),
  cancel: (id) => api.post(`/stock-transfers/${id}/cancel`)
};

// Categories API (Retail Module)
export const categoriesAPI = {
  getAll: (params) => api.get('/categories', { params }),
  getById: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
  reorder: (categories) => api.put('/categories/reorder', { categories })
};

// Stock Adjustments API (Retail Module)
export const stockAdjustmentsAPI = {
  getAll: (params) => api.get('/stock-adjustments', { params }),
  getSummary: (params) => api.get('/stock-adjustments/summary', { params }),
  getById: (id) => api.get(`/stock-adjustments/${id}`),
  getByProduct: (productId, params) => api.get(`/stock-adjustments/product/${productId}`, { params }),
  create: (data) => api.post('/stock-adjustments', data),
  bulkCount: (data) => api.post('/stock-adjustments/bulk', data)
};

// Returns API (Retail Module)
export const returnsAPI = {
  getAll: (params) => api.get('/returns', { params }),
  getById: (id) => api.get(`/returns/${id}`),
  getSaleForReturn: (saleId) => api.get(`/returns/sale/${saleId}`),
  lookupSale: (query) => api.get('/returns/lookup/search', { params: { q: query } }),
  create: (data) => api.post('/returns', data),
  updateStatus: (id, data) => api.put(`/returns/${id}/status`, data),
  getStats: (params) => api.get('/returns/stats/summary', { params })
};

// Attendants API (Services Module)
export const attendantsAPI = {
  getAll: (params) => api.get('/attendants', { params }),
  getById: (id, params) => api.get(`/attendants/${id}`, { params }),
  create: (data) => api.post('/attendants', data),
  update: (id, data) => api.put(`/attendants/${id}`, data),
  delete: (id) => api.delete(`/attendants/${id}`),
  getPerformanceSummary: (params) => api.get('/attendants/performance/summary', { params }),
  uploadImage: (id, formData) => api.post(`/attendants/${id}/upload-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
};

// Branches API
export const branchesAPI = {
  getAll: () => api.get('/branches'),
  getById: (id) => api.get(`/branches/${id}`),
  create: (data) => api.post('/branches', data),
  update: (id, data) => api.put(`/branches/${id}`, data),
  setMain: (id) => api.post(`/branches/${id}/set-main`),
  delete: (id, data) => api.delete(`/branches/${id}`, { data }),
  getStats: (params) => api.get('/branches/stats/overview', { params }),
  // Branch Requests
  getRequests: (params) => api.get('/branches/requests/list', { params }),
  createRequest: (data) => api.post('/branches/requests', data),
  cancelRequest: (id) => api.delete(`/branches/requests/${id}`)
};

// Restaurant Tables API
export const tablesAPI = {
  getAll: (params) => api.get('/tables', { params }),
  getById: (id) => api.get(`/tables/${id}`),
  create: (data) => api.post('/tables', data),
  update: (id, data) => api.put(`/tables/${id}`, data),
  delete: (id) => api.delete(`/tables/${id}`),
  updateStatus: (id, status) => api.put(`/tables/${id}/status`, { status }),
  bulkCreate: (data) => api.post('/tables/bulk', data),
  updatePositions: (positions) => api.put('/tables/positions/update', { positions })
};

// Restaurant Orders API
export const ordersAPI = {
  getAll: (params) => api.get('/orders', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  getOpenTabs: (params) => api.get('/orders/open-tabs', { params }),
  getCancelled: (params) => api.get('/orders/cancelled', { params }),
  getKitchenRejections: (params) => api.get('/orders/kitchen-rejections', { params }),
  create: (data) => api.post('/orders', data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  addItems: (id, items) => api.post(`/orders/${id}/items`, { items }),
  updateStatus: (id, status) => api.put(`/orders/${id}/status`, { status }),
  updatePriority: (id, priority) => api.put(`/orders/${id}/priority`, { priority }),
  closeTab: (id, data) => api.put(`/orders/${id}/close`, data),
  cancel: (id, reason) => api.delete(`/orders/${id}`, { data: { reason } }),
  updateItem: (orderId, itemId, data) => api.put(`/orders/${orderId}/items/${itemId}`, data),
  removeItem: (orderId, itemId) => api.delete(`/orders/${orderId}/items/${itemId}`),
  // Table-level operations
  getTableSummary: (tableId) => api.get(`/orders/table/${tableId}/summary`),
  closeTable: (tableId, data) => api.put(`/orders/table/${tableId}/close`, data),
  refreshOrder: (id) => api.get(`/orders/${id}/refresh`),
  // Switch table and cancel request
  switchTable: (id, newTableId) => api.put(`/orders/${id}/switch-table`, { newTableId }),
  requestCancel: (id, reason) => api.post(`/orders/${id}/request-cancel`, { reason }),
  // Direct cancel (for pending orders or kitchen reject)
  cancelDirect: (id, reason) => api.post(`/orders/${id}/cancel-direct`, { reason })
};

// Kitchen Display System API
export const kdsAPI = {
  getOrders: (params) => api.get('/kds/orders', { params }),
  getReadyOrders: (params) => api.get('/kds/orders/ready', { params }),
  getOrderDetails: (id) => api.get(`/kds/orders/${id}`),
  updateOrderStatus: (id, status) => api.put(`/kds/orders/${id}/status`, { status }),
  updateItemStatus: (id, status) => api.put(`/kds/items/${id}`, { status }),
  bumpOrder: (id) => api.post(`/kds/orders/${id}/bump`),
  recallOrder: (id, reason) => api.post(`/kds/orders/${id}/recall`, { reason }),
  rejectOrder: (id, reason) => api.post(`/kds/orders/${id}/reject`, { reason }),
  getStats: (params) => api.get('/kds/stats', { params })
};

// Product Modifiers API
export const modifiersAPI = {
  getForProduct: (productId) => api.get(`/modifiers/products/${productId}/modifiers`),
  getAll: (params) => api.get('/modifiers', { params }),
  create: (productId, data) => api.post(`/modifiers/products/${productId}/modifiers`, data),
  update: (id, data) => api.put(`/modifiers/${id}`, data),
  delete: (id) => api.delete(`/modifiers/${id}`),
  reorder: (productId, modifierIds) => api.post('/modifiers/reorder', { productId, modifierIds }),
  copy: (sourceProductId, targetProductId) => api.post('/modifiers/copy', { sourceProductId, targetProductId })
};

// Raw Materials API (Recipe/BOM)
export const rawMaterialsAPI = {
  getAll: (params) => api.get('/raw-materials', { params }),
  getById: (id) => api.get(`/raw-materials/${id}`),
  create: (data) => api.post('/raw-materials', data),
  update: (id, data) => api.put(`/raw-materials/${id}`, data),
  delete: (id) => api.delete(`/raw-materials/${id}`),
  restock: (id, data) => api.post(`/raw-materials/${id}/restock`, data),
  getHistory: (id, limit) => api.get(`/raw-materials/${id}/history`, { params: { limit } }),
  getLowStock: (params) => api.get('/raw-materials/low-stock', { params }),
  getCategories: () => api.get('/raw-materials/categories'),
  createAdjustment: (data) => api.post('/raw-materials/adjustments', data),
  search: (q, branchId) => api.get('/raw-materials/search/autocomplete', { params: { q, branchId } })
};

// Recipes API (Recipe/BOM)
export const recipesAPI = {
  get: (productId) => api.get(`/recipes/${productId}`),
  save: (productId, data) => api.post(`/recipes/${productId}`, data),
  delete: (productId) => api.delete(`/recipes/${productId}`),
  getCost: (productId) => api.get(`/recipes/${productId}/cost`),
  copy: (productId, targetProductId) => api.post(`/recipes/${productId}/copy`, { targetProductId }),
  getProductsWithoutRecipes: (params) => api.get('/recipes/products/without-recipes', { params }),
  getCostingReport: (params) => api.get('/recipes/reports/costing', { params })
};

// Raw Material Reports API
export const rawMaterialReportsAPI = {
  getConsumption: (params) => api.get('/raw-material-reports/consumption', { params }),
  getVariance: (params) => api.get('/raw-material-reports/variance', { params }),
  getCostAnalysis: (params) => api.get('/raw-material-reports/cost-analysis', { params }),
  getExpiring: (params) => api.get('/raw-material-reports/expiring', { params })
};

export default api;
